import api from "./api";

export interface SalaryCalculationResponse {
  results: SalaryRecord[];
}

export interface SalaryRecord {
  salary_id: string | number;
  salarytID?: string | number;
  employee_id: string;
  emptID?: string;
  employeeID: string;
  name: string;
  profile_photo_url?: string | null;
  calculationType: string;
  mode: string;
  status: string;
  department: string;
  designation: string;
  joiningDate: string;
  SalaryDate: string;
  month: number;
  year: number;
  monthlySalary: number;
  // Declared monthly take-home (net after TDS/PF/ESIC/PT/LWF), shown in the
  // Monthly column. Null when the employee has no declaration for the period.
  takeHome?: number | null;
  // Which calculation produced the row: "attendance", "no_deduction" (the
  // month bypasses attendance) or "no_deduction_attendance" (full pay, real
  // attendance kept). Older rows / the workflow calc leave it unset.
  method?: string | null;
  netSalary: number;
  // Why the net is smaller than the days worked would suggest. Null when the
  // month had no late-coming charge / no hours shortfall.
  lateComing?: {
    late_days: number;
    charged_days: number;
    amount: number;
    action: "hold" | "deduct" | "none";
    hold_balance: number;
    released: number;
  } | null;
  hoursShortfall?: {
    required: number;
    worked: number;
    shortfall: number;
    amount: number;
  } | null;
  bonus: number;
  extrapay: number;
  deductions: number;
  isApproved: boolean;
  // Approval-lifecycle flags returned by the calc endpoints (Status column).
  approved?: boolean;
  sent_for_approval?: boolean;
  approvedBy: string | null;
  approvedAt: string | null;
  createdAt: string;
  updatedAt: string;
  logs?: {
    attendance?: DailyBreakdown[];
  };

}

export interface DailyBreakdown {
  date: string;
  hours: number;
  status: string;
  payable: number;
  overtime: number;
  day_salary: number;
  overtime_amount: number;
  // Present in the stored logs all along — the payroll calc writes them on
  // every day — but never declared, so the UI could not show WHICH days were
  // late or what time the punch was.
  late?: boolean;
  clock_in?: string | null;
  clock_out?: string | null;
}

export interface SalaryCalculationLogsResponse {
  success?: boolean;
  salary_id?: string | number;
  employee_id?: string;
  employeeID?: string;
  name?: string;
  month?: number;
  year?: number;
  attendance: DailyBreakdown[];
  summary?: Record<string, number>;
}

export interface ApprovalRequest {
  id: string[];
  year: number;
  month: number;
}

export interface ApprovalResponse {
  success: boolean;
  message: string;
  results?: SalaryRecord[];
  data?: SalaryRecord;
  recipient_count?: number;
  employee_ids?: string[];
  skipped_employee_ids?: string[];
}

export interface SalaryStatusResponse {
  success: boolean;
  status: {
    pending: number;
    approved: number;
    rejected: number;
    total: number;
  };
  results?: SalaryRecord[];
}

export interface ManualSalaryAdjustment {
  employee_id: string;
  bonus: number;
  extrapay: number;
  month: number;
  year: number;
  description?: string;
}

export interface ExportEmployeesResponse {
  success: boolean;
  message: string;
  data?: any;
  download_url?: string;
}

export interface ExportSalaryResponse {
  success: boolean;
  message: string;
  data?: any;
  download_url?: string;
}

// Hourly vs monthly pay basis. Stored per employee in `salary_settings`
// (`emptID` set); employees with no row follow the branch-wide default.
export type RateType = 'hourly' | 'monthly';

export interface EmployeeRateTypesResponse {
  success: boolean;
  data: {
    default: RateType;
    overrides: Record<string, RateType>;
  };
}

// ---- Salary payment status (Payroll Approval → Upload Bank Statement) ----
// Recorded from the bank statement by /salary/payments/statement/apply or by
// hand via /salary/payments/mark; get_approved_salary rows carry it as `payment`.
export interface SalaryPayment {
  status: "PAID" | "RETURNED";
  amount: number | null;
  utr: string | null;
  txn_date: string | null;
  source: "statement" | "manual";
  return_reason: string | null;
  note: string | null;
  statement_name: string | null;
  recorded_at: string | null;
}

// One transaction read off the uploaded statement.
export interface StatementLineInfo {
  key: string; // sent back to apply to say which line pays a salary
  utr: string | null;
  txn_date: string | null;
  amount: number;
  kind: string; // NEFT / RTGS / IMPS / RETURN / OTHER
  beneficiary: string | null;
  ifsc: string | null;
  remark: string | null;
  narration: string;
}

export type StatementMatchResult = "matched" | "review" | "returned" | "not_found" | "already_paid";

export interface StatementMatchRow {
  salary_id: string;
  emptID: string;
  employeeID: string;
  name: string;
  net_salary: number;
  payment: SalaryPayment | null;
  result: StatementMatchResult;
  candidates: (StatementLineInfo & { reasons: string[] })[];
  returned: (StatementLineInfo & { reason: string | null; returned_on: string | null }) | null;
  // A credit of this salary's amount came back without saying which transfer it was.
  possible_return?: StatementLineInfo | null;
}

export interface StatementPreview {
  success: boolean;
  year: number;
  month: number;
  statement: {
    file_name: string | null;
    bank?: string; // the bank it was read as — see STATEMENT_BANKS in BankStatementUploadDialog
    transactions: number;
    from: string | null;
    to: string | null;
  };
  summary: Record<StatementMatchResult, number>;
  rows: StatementMatchRow[];
  unmatched_salary_debits: StatementLineInfo[];
}

export interface StatementApplyResult {
  success: boolean;
  paid: number;
  returned: number;
  failed: number;
  results: { salary_id: string; success: boolean; status?: string; message?: string }[];
}


export const salaryApi = {
  // Salary settings for the current branch — includes the holiday calendar.
  getSettings: () => api.get('/salary/get_salary_settings'),

  // Branch default Hourly/Monthly rate type + every per-employee override.
  getEmployeeRateTypes: () =>
    api.get<EmployeeRateTypesResponse>('/salary/employee_rate_types'),

  // Set the Hourly/Monthly override for employees. `rateType: null` clears it,
  // so those employees follow the company-wide setting again.
  setEmployeeRateType: (employeeIds: string[], rateType: RateType | null) =>
    api.post('/salary/set_employee_rate_type', {
      employee_ids: employeeIds,
      rate_type: rateType,
    }),

  // Employee self-service: the logged-in user's own salary for a period.
  // Only returns the breakdown once the salary has been sent for approval.
  getMySalary: (year: string, month: string) =>
    api.get(`/salary/my_salary?year=${year}&month=${month}`),

  // The employee's own answer about a closed month's overtime: take it as
  // compensatory leave ("leave", which their reporting person still decides),
  // or keep it as earnings ("pay", recorded so the month stops asking).
  // Offered only while the month is sent for approval — see overtime_choice
  // on the getMySalary response.
  setOvertimeChoice: (year: number, month: number, choice: "leave" | "pay") =>
    api.post(`/salary/overtime_choice`, { year, month, choice }),

  // Leaves the approver carried past the employee's balance, which the
  // employee has not yet said how to settle. Not month-scoped: the period to
  // work the hours back in is usually a LATER month than the leave, so asking
  // only on the leave's own payslip would reach them too late to act.
  getLeaveRecoveryChoices: () => api.get(`/salary/leave_recovery_choices`),

  // "hours" works them back inside the approver's period; "money" settles it
  // in pay, as unpaid days on the month the leave falls in.
  setLeaveRecoveryChoice: (leaveId: string, choice: "hours" | "money") =>
    api.put(`/salary/leave_recovery_choice`, { leave_id: leaveId, choice }),

  // Original attendance-based calculation.
  //
  // `overrideSaved` says the ADMIN picked this calculation, and sends
  // `preserve_saved=false`. Leave it off for the page's own refresh: the
  // server then returns a row already sent for approval untouched, instead of
  // rewriting the figures the approver and the employee are both looking at.
  calculateBulk: (
    year: string,
    month: string,
    employeeIds: string[],
    overrideSaved = false
  ) => {
    return api.post(
      `/salary/calculate/bulk?year=${year}&month=${month}${overrideSaved ? "&preserve_saved=false" : ""}`,
      { id: employeeIds }
    );
  },

  // New system-level tracking calculation
  calculateWithSystemTracking: (
    year: string,
    month: string,
    employeeIds: string[],
    overrideSaved = false
  ) => {
    return api.post(
      `/salary/calculate/bulk/system-tracking?year=${year}&month=${month}${overrideSaved ? "&preserve_saved=false" : ""}`,
      { id: employeeIds }
    );
  },

  getCalculationLogs: (salaryId: string | number) =>
    api.get<SalaryCalculationLogsResponse>(
      `/salary/calculation/${encodeURIComponent(String(salaryId))}/logs`
    ),

  manualSalaryAdjustment: (data: ManualSalaryAdjustment) => {
    return api.put('/salary/manual_salary_adjustment', data);
  },

  // No Deduction - Attendance Based
calculateNoDeduction: (year: string, month: string, employeeIds: string[]) => {
  return api.post(
    `/salary/re-calculate_no_deduction?year=${year}&month=${month}`,
    { id: employeeIds }
  );
},

// No Deduction WITH attendance: full pay, but the record keeps the clocked
// attendance (present / effective days, overtime, lates) instead of a month
// of synthetic Present rows.
calculateNoDeductionWithAttendance: (year: string, month: string, employeeIds: string[]) => {
  return api.post(
    `/salary/re-calculate_no_deduction/attendance?year=${year}&month=${month}`,
    { id: employeeIds }
  );
},

// No Deduction - System Tracking
calculateNoDeductionSystem: (year: string, month: string, employeeIds: string[]) => {
  return api.post(
    `/salary/re-calculate_no_deduction/system-tracking?year=${year}&month=${month}`,
    { id: employeeIds }
  );
},


  // Get Approved Salary - POST API
  getApprovedSalaries: (year: string, month: string, employeeIds: string[] = []) => {
    return api.post<ApprovalResponse>(
      `/salary/get_approved_salary?year=${year}&month=${month}`,
      { id: employeeIds }
    );
  },

  // Approve Salary - PUT API
  approveSalaries: (year: string, month: string, employeeIds: string[]) => {
    return api.put<ApprovalResponse>(
      `/salary/approved_salary?year=${year}&month=${month}`,
      { id: employeeIds }
    );
  },

  // Move approved salaries back to the pending approval queue.
  revertApprovedSalaries: (year: string, month: string, employeeIds: string[]) => {
    return api.put<ApprovalResponse>(
      `/salary/revert_approved_salary?year=${year}&month=${month}`,
      { id: employeeIds }
    );
  },

  // Remind payroll approvers only for salaries that were already submitted.
  sendApprovalReminder: (year: string, month: string, employeeIds: string[]) => {
    return api.post<ApprovalResponse>(
      `/salary/review-reminder?year=${year}&month=${month}`,
      { id: employeeIds }
    );
  },

  // Get Salary Status - POST API
  getSalaryStatus: (year: string, month: string, employeeIds: string[] = []) => {
    return api.post<SalaryStatusResponse>(
      `/salary/get_salary_status?year=${year}&month=${month}`,
      { id: employeeIds }
    );
  },

  // Reject Salaries — optional free-text reason is persisted on the record.
  rejectSalaries: (year: string, month: string, employeeIds: string[], reason?: string) => {
    return api.put<ApprovalResponse>(
      `/salary/reject_salary?year=${year}&month=${month}`,
      { id: employeeIds, reason }
    );
  },

  // Send generated salaries for approval (notifies employees; status stays PENDING)
  sendForApproval: (year: string, month: string, employeeIds: string[]) =>
    api.post(`/salary/send_for_approval?year=${year}&month=${month}`, { id: employeeIds }),

 exportEmployees: () => {
  return api.get(`/export_employees`);
},

  // NEW: Export Salary - POST API
  exportSalary: (year: string, month: string, employeeIds: string[] = []) => {
    return api.post<ExportSalaryResponse>(
      `/export_salary?year=${year}&month=${month}`,
      { id: employeeIds }
    );
  },

  // Download the bank bulk salary-upload sheet (.xlsx) in the plain
  // SNO/AMOUNT/DATE/DEBIT ACCOUNT NO/... column format banks expect for NEFT/RTGS
  // bulk uploads. debitAccountNo + senderAddress are printed on every row and
  // are supplied by the caller at export time (not stored). Returns the file
  // as a Blob so the caller can trigger a browser download.
  // `bank` selects the sheet layout ("kotak" | "au" | future). `fields` carries
  // the bank-specific values typed on the fly at export time (debit_account_no,
  // sender_address, payment_method_name, remitter_name, payment_instructions,
  // ccy, ...) — each is written to every row of the sheet as-is by the backend.
  exportSalaryTransferSheet: (
    year: string,
    month: string,
    employeeIds: string[] = [],
    bank: string = "kotak",
    fields: Record<string, string> = {}
  ) => {
    const params = new URLSearchParams({ year, month, bank });
    Object.entries(fields).forEach(([key, value]) => {
      if (value != null && value !== "") params.append(key, value);
    });
    return api
      .post(
        `/salary/export_salary_transfer_sheet?${params.toString()}`,
        { id: employeeIds },
        { responseType: "blob" }
      )
      .then((res) => res.data as Blob);
  },

  // ---- Payment status from the bank statement ----
  // Reads the statement and proposes a debit for each approved salary of the
  // month. Writes nothing.
  previewBankStatement: (year: string, month: string, bank: string, file: File) => {
    const form = new FormData();
    form.append("file", file);
    return api
      .post<StatementPreview>(
        `/salary/payments/statement/preview?year=${year}&month=${month}&bank=${encodeURIComponent(bank)}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      )
      .then((res) => res.data);
  },

  // Records the rows the user kept. The same file goes up again, read as the
  // same bank: the server re-runs the match and only accepts a debit it offered
  // for that salary.
  applyBankStatement: (
    year: string,
    month: string,
    bank: string,
    file: File,
    selections: { salary_id: string; key: string }[]
  ) => {
    const form = new FormData();
    form.append("file", file);
    form.append("selections", JSON.stringify(selections));
    return api
      .post<StatementApplyResult>(
        `/salary/payments/statement/apply?year=${year}&month=${month}&bank=${encodeURIComponent(bank)}`,
        form,
        { headers: { "Content-Type": "multipart/form-data" } }
      )
      .then((res) => res.data);
  },

  // Manual Paid / Unpaid — cash, cheque, or undoing a wrong match.
  markSalaryPayment: (
    year: string,
    month: string,
    employeeIds: string[],
    status: "PAID" | "UNPAID",
    details: { reference?: string; paid_on?: string; note?: string } = {}
  ) =>
    api.post(`/salary/payments/mark?year=${year}&month=${month}`, {
      id: employeeIds,
      status,
      ...details,
    }),

  // ---- Retainer-ship ----
  getRetainer: (employeeId: string) =>
    api.get(`/salary/retainer/${employeeId}`),

  updateRetainer: (employeeId: string, data: any) =>
    api.put(`/salary/retainer/${employeeId}`, data),

  releaseRetainer: (employeeId: string, hold = false) =>
    api.post(`/salary/retainer/${employeeId}/release`, { hold }),

  getRetainerReport: () =>
    api.get(`/salary/retainer/report`),

};

// ---- Salary notifications (bell) ----
export const getSalaryNotifications = (limit = 50) =>
  api.get(`/salary/salary_notifications/unread?limit=${limit}`);

export const markSalaryNotificationRead = (id: string) =>
  api.post(`/salary/salary_notifications/${id}/read`);
